import type { ComponentProps, FC, PropsWithChildren } from 'react';

import { COLOR } from '#@/shared/utils/styles';

import StandardButton from '../Button';
import ButtonGroup from '../ButtonGroup';
import type { IconProps } from '../Icon';
import * as Styles from './Styles';

interface MainProps {
  alignContent?: 'left' | 'center';
}

const Main: FC<PropsWithChildren<MainProps>> = ({ alignContent = 'left', ...props }) => (
  <Styles.Main $alignContent={alignContent} {...props} />
);

type ButtonProps = Omit<ComponentProps<typeof StandardButton>, 'variant'>;

const Button: FC<ButtonProps> = (props) => (
  <ButtonGroup alignContentWithText>
    <StandardButton {...props} variant="secondaryBrand" />
  </ButtonGroup>
);

interface BulletpointProps {
  icon: IconProps;
}

export const Bulletpoint: FC<PropsWithChildren<BulletpointProps>> = ({
  icon,
  children,
  ...props
}) => (
  <Styles.Bulletpoint {...props}>
    <Styles.BulletpointIcon {...icon} />
    <div>{children}</div>
  </Styles.Bulletpoint>
);

interface CallToActionProps {
  size?: 'regular' | 'large';
}

export const CallToAction: FC<PropsWithChildren<CallToActionProps>> = ({
  size = 'regular',
  children,
  ...props
}) => {
  switch (size) {
    case 'regular':
      return <Styles.CallToActionRegular {...props}>{children}</Styles.CallToActionRegular>;
    case 'large':
      return <Styles.CallToActionLarge {...props}>{children}</Styles.CallToActionLarge>;
  }
};

interface ExplanationProps {
  color?: COLOR;
}

export const Explanation: FC<PropsWithChildren<ExplanationProps>> = ({
  color = COLOR.textTertiary,
  children,
  ...props
}) => (
  <Styles.Explanation $color={color} {...props}>
    {children}
  </Styles.Explanation>
);

export interface EmptyContentProps {
  layout?: 'oneColumn' | 'twoColumns';
}

const EmptyContent: FC<PropsWithChildren<EmptyContentProps>> = ({
  layout = 'oneColumn',
  children,
  ...props
}) => {
  switch (layout) {
    case 'oneColumn':
      return (
        <Main alignContent="center" {...props}>
          {children}
        </Main>
      );
    case 'twoColumns':
      return <Styles.TwoColumnsLayout {...props}>{children}</Styles.TwoColumnsLayout>;
  }
};

export interface Subcomponents {
  Main: typeof Main;
  Illustration: typeof Styles.Illustration;
  SidebarIllustration: typeof Styles.SidebarIllustration;
  CallToAction: typeof CallToAction;
  Explanation: typeof Explanation;
  Button: typeof Button;
  ActionCardContainer: typeof Styles.ActionCardContainer;
  Bulletpoint: typeof Bulletpoint;
}

const defineSubcomponent = <T extends keyof Subcomponents>(
  name: T,
  component: (typeof EmptyContent & Subcomponents)[T],
) => {
  (EmptyContent as typeof EmptyContent & Subcomponents)[name] = component;
  (EmptyContent as typeof EmptyContent & Subcomponents)[name].displayName = `EmptyContent.${name}`;
};

defineSubcomponent('Main', Main);
defineSubcomponent('Illustration', Styles.Illustration);
defineSubcomponent('SidebarIllustration', Styles.SidebarIllustration);
defineSubcomponent('CallToAction', CallToAction);
defineSubcomponent('Explanation', Explanation);
defineSubcomponent('Button', Button);
defineSubcomponent('ActionCardContainer', Styles.ActionCardContainer);
defineSubcomponent('Bulletpoint', Bulletpoint);

export default EmptyContent as typeof EmptyContent & Subcomponents;
